import { html, state } from 'lit-element'
import { t } from '../../directives/translate'
import { repeat } from 'lit-html/directives/repeat'
import presenceSelectorPopoverStyles from './presence-selector-popover.styles.scss'
import { SVG_CHEVRON_DOWN_OUTLINED } from '@getgo/chameleon-icons'

import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import { getShellApiInstance } from '../../common/shell-api-helpers'
import type { Listener } from '../../services/namespaces'
import type {
  PresenceEvents,
  PresenceUpdated,
  ShellPresenceService,
  UserPresence,
  UserPresenceUpdated,
} from '../../services/presence'
import {
  Appearance,
  UserAppearance,
  UserDoNotDisturb,
  UserStatus,
  convertAppearanceToPresence,
  PresenceServiceNamespace,
} from '../../services/presence'
import { getAppearanceTranslationKey } from '../../common/presence-helpers'
import { getShellLogger } from '../../common/logger'
import { assert } from '../../common/assert-helpers'
import { getFeatureFlagValue } from '../../services/feature-flags'
import { FeatureFlagsVariations } from '../../services/feature-flags/models'
import { nothing } from 'lit-html'
import { ShellElement } from '../../common/shell-element'

export class GotoPresenceSelectorPopover extends ShellElement {
  static readonly tagName = 'goto-presence-selector-popover'

  private readonly currentUserExternalUserKey = getShellApiInstance().user.key ?? ''
  private readonly presence = getShellApiInstance().presence
  @state() private presenceStatus = getAppearanceTranslationKey(Appearance.OFFLINE, true)
  private readonly appearanceList = [
    Appearance.ONLINE,
    Appearance.AWAY,
    Appearance.BUSY,
    Appearance.DO_NOT_DISTURB,
    Appearance.OFFLINE,
  ]

  static get styles() {
    return presenceSelectorPopoverStyles
  }

  connectedCallback() {
    super.connectedCallback()
    if (this.currentUserExternalUserKey && this.presence) {
      this.subscribeToPresence(this.presence)
      this.listenForPresenceUpdatedEvent()
    }
  }

  private subscribeToPresence(presence: ShellPresenceService) {
    presence
      .subscribe(this.currentUserExternalUserKey)
      .then(() => {
        this.unsubscribeFunctions.push(() => {
          presence.unsubscribe(this.currentUserExternalUserKey)
        })
      })
      .catch(() => {
        getShellLogger().error(
          `failed to subscribe to presence for external user key ${this.currentUserExternalUserKey}`,
        )
      })
  }

  private listenForPresenceUpdatedEvent() {
    const { eventBus } = getShellApiInstance()
    const { userPresenceUpdate } = eventBus.subscribeTo<typeof PresenceServiceNamespace, typeof PresenceEvents>(
      PresenceServiceNamespace,
    )

    const presenceUpdatedHandler: Listener<PresenceUpdated> = (payload: UserPresenceUpdated) => {
      const { appearance } = payload
      if (appearance) {
        this.presenceStatus = getAppearanceTranslationKey(appearance, true)
      }
    }

    userPresenceUpdate.on(presenceUpdatedHandler)

    this.unsubscribeFunctions.push(() => {
      userPresenceUpdate.removeListener(presenceUpdatedHandler)
    })
  }

  private setNewPresence(userPresence: UserPresence) {
    try {
      assert(this.presence, 'shell presence service is undefined')
      this.presence.setUserPresence(userPresence)
    } catch {
      //to be handled in https://jira.ops.expertcity.com/browse/SCORE-1417
    }
  }

  private setArgumentsForNewPresence(appearance: Appearance) {
    switch (appearance) {
      case Appearance.ONLINE:
        this.setNewPresence({
          userAppearance: UserAppearance.NONE,
          userDoNotDisturb: UserDoNotDisturb.NONE,
          userStatus: UserStatus.NONE,
        })
        break
      case Appearance.AWAY:
        this.setNewPresence({
          userAppearance: UserAppearance.AWAY,
          userDoNotDisturb: UserDoNotDisturb.NONE,
          userStatus: UserStatus.NONE,
        })
        break
      case Appearance.BUSY:
        this.setNewPresence({
          userAppearance: UserAppearance.BUSY,
          userDoNotDisturb: UserDoNotDisturb.NONE,
          userStatus: UserStatus.NONE,
        })
        break
      case Appearance.DO_NOT_DISTURB:
        this.setNewPresence({
          userAppearance: UserAppearance.NONE,
          userDoNotDisturb: UserDoNotDisturb.DO_NOT_DISTURB,
          userStatus: UserStatus.NONE,
        })
        break
      case Appearance.OFFLINE:
        this.setNewPresence({
          userAppearance: UserAppearance.OFFLINE,
          userDoNotDisturb: UserDoNotDisturb.NONE,
          userStatus: UserStatus.NONE,
        })
        break
    }
  }

  private renderPresenceMenuItem(appearance: Appearance) {
    if (appearance === Appearance.BUSY && !getFeatureFlagValue(FeatureFlagsVariations.SHELL_ADVANCED_PRESENCE_BUSY)) {
      return nothing
    }
    return html`
      <chameleon-menu-item
        data-test=${appearance}
        @menuitemclick=${() => {
          this.dispatchEvent(
            new CustomEvent('presenceClicked', { detail: { presence: appearance }, bubbles: true, composed: true }),
          )

          this.setArgumentsForNewPresence(appearance)
        }}
        aria-label=${t(getAppearanceTranslationKey(appearance, true))}
      >
        ${t(getAppearanceTranslationKey(appearance, true))}
        <chameleon-presence-indicator
          presence=${convertAppearanceToPresence(appearance)}
          slot="start"
        ></chameleon-presence-indicator>
      </chameleon-menu-item>
    `
  }

  render() {
    return html`
      <chameleon-popover position="bottom-center" menu data-test="presence-selector-popover-menu">
        <div class="presence-menu-trigger" slot="trigger">
          <chameleon-typography>${t(this.presenceStatus)}</chameleon-typography
          ><chameleon-svg slot="start">${unsafeSVG(SVG_CHEVRON_DOWN_OUTLINED)}</chameleon-svg>
        </div>
        <div slot="content">
          <chameleon-menu>
            ${repeat(
              this.appearanceList,
              appearance => appearance,
              appearance => this.renderPresenceMenuItem(appearance),
            )}
          </chameleon-menu>
        </div>
      </chameleon-popover>
    `
  }
}
declare global {
  interface HTMLElementTagNameMap {
    readonly [GotoPresenceSelectorPopover.tagName]: GotoPresenceSelectorPopover
  }
}
